import { FC, useEffect, useState } from 'react';
import { captureException } from '@sentry/react';
import { Avatar, Button, Col, Dropdown, Modal, Row, Typography } from 'antd';
import { TbPyramid, TbUserHexagon, TbUserPentagon, TbUserSquare } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { memberService } from '@/services';
import { DropdownMenuPanel } from '@/shared';
import DropdownButton from '@/shared/components/dropdowns/DropdownButton';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { Member, MemberPermissionType } from '@/types';

interface MemberPermissionModalProps {
    member?: Member;
    onUpdated: (member: Member) => void;
    open: boolean;
    setOpen: (open: boolean) => void;
}

const permissionOptions = [
    {
        description: 'Can view select workspace resources.',
        icon: <TbUserSquare size='1rem' />,
        label: 'Guest',
        value: MemberPermissionType.GUEST,
    },
    {
        description: "Can edit workspace resources they're invited to.",
        icon: <TbUserHexagon size='1rem' />,
        label: 'Member',
        value: MemberPermissionType.MEMBER,
    },
    {
        description: 'Full visibility and access to the workspace.',
        icon: <TbUserPentagon size='1rem' />,
        label: 'Admin',
        value: MemberPermissionType.ADMIN,
    },
];

const MemberPermissionModal: FC<MemberPermissionModalProps> = ({ member, onUpdated, open, setOpen }) => {
    const [permission, setPermission] = useState<MemberPermissionType>(
        member?.permission ?? MemberPermissionType.MEMBER,
    );
    const [saving, setSaving] = useState(false);
    const { token } = useAntDesignTheme();

    useEffect(() => {
        if (open) {
            setPermission(member?.permission ?? MemberPermissionType.MEMBER);
        }
    }, [member?.permission, open]);

    const handleSave = async () => {
        if (!member?.id) {
            return;
        }

        try {
            setSaving(true);
            const updatedMember = await memberService.updatePermission(member.id, permission);

            onUpdated(updatedMember);
            setOpen(false);
            toast('Member Permission Changed', {
                icon: <TbPyramid size='1rem' />,
                description: `${member.user?.firstName} ${member.user?.lastName} → ${permission}`,
            });
        } catch (error) {
            captureException(error);
            toast.error('Unable to Update Member Permission');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            destroyOnClose
            open={open}
            title='Change User Permission'
            onCancel={() => setOpen(false)}
            footer={[
                <Button key='cancel' onClick={() => setOpen(false)}>
                    Cancel
                </Button>,
                <Button key='save' loading={saving} type='primary' onClick={handleSave}>
                    Save Permission
                </Button>,
            ]}
        >
            <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
                <Avatar src={member?.user?.avatar} />
                <Typography.Title level={5} style={{ margin: '0.5rem 0 0' }}>
                    {member?.user?.firstName} {member?.user?.lastName}
                </Typography.Title>
                <small style={{ color: token.colorTextTertiary }}>{member?.user?.emailAddress}</small>
            </div>
            <Dropdown
                trigger={['click']}
                popupRender={() => (
                    <DropdownMenuPanel
                        {...{
                            selectedKeys: [permission],
                            onClick: ({ key }) => setPermission(key as MemberPermissionType),
                            items: permissionOptions.map((option) => ({
                                key: option.value,
                                icon: option.icon,
                                label: (
                                    <span>
                                        {option.label}
                                        <br />
                                        <small style={{ color: token.colorTextTertiary }}>{option.description}</small>
                                    </span>
                                ),
                            })),
                        }}
                    />
                )}
            >
                <DropdownButton block type='dashed'>
                    <Row align='middle' wrap={false}>
                        <Col flex='auto' style={{ textAlign: 'left' }}>
                            {permission}
                        </Col>
                    </Row>
                </DropdownButton>
            </Dropdown>
        </Modal>
    );
};

export default MemberPermissionModal;
